import './StreamingCreatives.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CommitAudioCompanion } from '@blisspointmedia/bpm-types/dist/Creative';
import { ApprovalStages, Reviewer } from '@blisspointmedia/bpm-types/dist/CreativeApprovals';
import { TODAY } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { Button } from '@Components/Button/Button';
import { DatePicker } from '@Components/DatePicker';
import { Appearance, Design, Intent, Placement, Size } from '@Components/designConstants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Page } from '@Components/Page';
import { FullPageSpinner } from '@Components/Spinner';
import { Neutral100 } from '@src/utils/colors';
import { equalsIgnoreCase } from '@src/utils/text-utils';
import { MediaType, useCreativeMap } from '../../redux/creative';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { useExperimentFlag } from '../../utils/experiment-context';
import { awaitJSON, CreativeLambdaFetch, StreamingLambdaFetch } from '../../utils/fetch-utils';
import { useAudioCompanionOptions } from '../../utils/hooks/useAudioCompanionOptions';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useList, useMap } from '../../utils/hooks/useData';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { ImportModal } from '../AdOps/StreamingTools/ImportModal';
import { Approvals } from './Approvals';
import { Cart } from './Cart';
import { Generate } from './Generate';
import { NonCart } from './NonCart';
import {
  ApprovalStageData,
  CartItem,
  NAVS,
  StreamingCreativesContext,
  StreamingCreativesContextValue,
  TabKey,
  TabKeyType,
  WorkspaceMetadata
} from './streaming-creatives-constants';
import {
  buildAudioCompanionMap,
  useShopData,
  useWorkspaceOptions
} from './streaming-creatives-utils';

interface LocalData {
  selectedMap?: Record<string, boolean>;
  workspaceMap?: Record<string, number>;
  workspaceMetadataMap?: Record<string, WorkspaceMetadata>;
  cart?: CartItem[];
  date?: string;
}

export const StreamingCreatives = () => {
  const enableImporter = useExperimentFlag('enableStreamingCreativesImporter');
  const { cid: company } = useCompanyInfo();
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const [showImportChangesModal, setShowImportChangesModal] = useState(false);
  const { tab, goToTab } = useTabbedNav<TabKeyType>({
    baseURL: 'streaming/creatives',
    defaultKey: TabKey.MENU,
    navs: NAVS
  });

  const [platform, setPlatform] = useState('');
  const [adServer, setAdServer] = useState('');
  const [dimensions, setDimensions] = useState('');
  const [length, setLength] = useState<string | null>(null);
  const [flashtalkingPlacementType, setFlashtalkingPlacementType] = useState('');

  const mediaTypes: MediaType[] = useMemo(() => {
    const types: MediaType[] = ['streaming', 'audio'];
    if (equalsIgnoreCase(adServer, 'ft') || equalsIgnoreCase(adServer, 'flashtalking')) {
      types.push('display');
    }
    return types;
  }, [adServer]);
  const {
    creativeMap,
    colorMap,
    isciLiveOnDate,
    loaded: creativeMapLoaded
  } = useCreativeMap({ company, mediaTypes });

  const [date, setDate] = useState(TODAY);

  const [shopData, refetchShopData] = useShopData();

  // Map of id to boolean of which placements are selected.
  const [selectedMap, setSelectedValue, setSelectedMap] = useMap<string, boolean>();
  const workspaceOptions = useWorkspaceOptions(
    date,
    length,
    platform,
    adServer,
    dimensions,
    flashtalkingPlacementType,
    creativeMap,
    colorMap,
    isciLiveOnDate
  );

  // Map of isci to value.
  const [workspaceMap, setWorkspaceValue, setWorkspaceMap] = useMap<string, number>();
  const [workspaceMetadataMap, setWorkspaceMetadata, setWorkspaceMetadataMap] = useMap<
    string,
    WorkspaceMetadata
  >();

  // Imported file data.
  const [file, setFile] = useState<File>();

  const audioCompanionOptions = useAudioCompanionOptions();

  const [cart, setCart, addToCart] = useList<CartItem>();

  const inCart = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const cartItem of cart) {
      for (const id of cartItem.placements) {
        map[id] = true;
      }
    }
    return map;
  }, [cart]);

  const [localLoaded, setLocalLoaded] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);

  const localStorageKey = `BPMStreamingCreatives${company}`;

  const clearWorkspace = useCallback(() => {
    setSelectedMap({});
    setCart([]);
    setWorkspaceMap({});
    setWorkspaceMetadataMap({});
    setPlatform('');
    setAdServer('');
    setDimensions('');
    setLength(null);
    setFlashtalkingPlacementType('');
  }, [setCart, setSelectedMap, setWorkspaceMap, setWorkspaceMetadataMap]);

  useEffect(() => {
    if (company && shopData && creativeMap && !localLoaded && !localLoading) {
      setLocalLoading(true);
      (async () => {
        const localData: LocalData = JSON.parse(
          window.localStorage.getItem(localStorageKey) || '{}'
        );

        const isEmpty = (data: LocalData) =>
          !(
            Object.keys(data.workspaceMap ?? {}).length ||
            Object.keys(data.selectedMap ?? {}).length ||
            (data.cart ?? []).length
          );
        // If their local storage is empty, don't do anything.
        if (isEmpty(localData)) {
          setLocalLoaded(true);
          setLocalLoading(false);
          return;
        }

        let localDate = localData.date || date;

        if (localDate < TODAY) {
          localDate = TODAY;
          await setError({
            title: 'Date Was Reset',
            intent: Intent.WARNING,
            message:
              'You had items in your cart or workspace, but the rotation date you had is now in the past. The date has been updated to today. Make sure the items in your cart and workspace are still valid.'
          });
        }

        const badIscis: string[] = [];
        const badPlacements: string[] = [];

        const newWorkspaceMap: Record<string, number> = {};
        const oldWorkspaceMap = localData.workspaceMap || {};
        for (const isci of Object.keys(oldWorkspaceMap)) {
          if (isciLiveOnDate({ isci, date: localDate })) {
            newWorkspaceMap[isci] = oldWorkspaceMap[isci];
          } else {
            badIscis.push(isci);
          }
        }

        const newSelectedMap: Record<string, boolean> = {};
        const oldSelectedMap = localData.selectedMap || {};

        // Track gating values locally so we can validate compatibility for
        // subsequent placements without relying on async React state.
        let restoredPlatform: string | null = null;
        let restoredAdServer: string | null = null;
        let restoredDimensions = '';
        let restoredLength: string | null = null;
        let restoredFTPlacementType = '';

        for (const placementId of Object.keys(oldSelectedMap)) {
          if (!oldSelectedMap[placementId]) {
            continue;
          }
          const placement = shopData.placementMap[placementId];
          if (placement) {
            const placementPlatform = placement.platform.split(' ')[0];
            const placementDimensions = placementPlatform.includes('Display')
              ? (placement.rotationKey.match(/(\d+x\d+)/)?.[1] ?? '')
              : '';
            const placementLength = placement.length ? placement.length.replace(/s/g, '') : null;
            const placementFTType = placement.flashtalkingPlacementType || '';

            if (restoredPlatform !== null) {
              // Subsequent placement — only restore if compatible with the gate set by the first.
              if (
                placementPlatform === restoredPlatform &&
                placement.adServer === restoredAdServer &&
                (!restoredLength || placement.length === `${restoredLength}s`) &&
                (!restoredDimensions || placementDimensions === restoredDimensions) &&
                (!restoredFTPlacementType || placementFTType === restoredFTPlacementType)
              ) {
                newSelectedMap[placementId] = true;
              } else {
                badPlacements.push(placementId);
              }
            } else {
              // First valid placement — sets the gate for the rest.
              newSelectedMap[placementId] = true;
              restoredPlatform = placementPlatform;
              restoredAdServer = placement.adServer;
              restoredDimensions = placementDimensions;
              restoredLength = placementLength;
              restoredFTPlacementType = placementFTType;
            }
          } else {
            badPlacements.push(placementId);
          }
        }

        if (restoredPlatform !== null) {
          setPlatform(restoredPlatform);
          setAdServer(restoredAdServer ?? '');
          setDimensions(restoredDimensions);
          if (restoredLength) {
            setLength(restoredLength);
          }
          if (restoredFTPlacementType) {
            setFlashtalkingPlacementType(restoredFTPlacementType);
          }
        }

        const newCart: CartItem[] = [];

        for (const item of localData.cart || []) {
          const { placements, rotation } = item;
          let goodPlacements = true;
          for (const placement of placements) {
            if (!shopData.placementMap[placement]) {
              goodPlacements = false;
              badPlacements.push(placement);
            }
          }
          let goodRotation = true;
          for (const creative of rotation) {
            if (!isciLiveOnDate({ isci: creative.isci, date: localDate })) {
              goodRotation = false;
              badIscis.push(creative.isci);
            }
          }
          if (goodRotation && goodPlacements) {
            newCart.push(item);
          }
        }

        if (badPlacements.length || badIscis.length) {
          const lines: string[] = [];
          if (badPlacements.length) {
            lines.push(
              `The following tags are no longer active:\n${[...new Set(badPlacements)].join(', ')}`
            );
          }
          if (badIscis.length) {
            if (lines.length) {
              lines.push('\n');
            }
            lines.push(
              `The following ISCIs are no longer live:\n${[...new Set(badIscis)].join(', ')}`
            );
          }
          lines.push(
            '\nSome items in your cart had this ISCI and have been removed. Double check before committing your changes.'
          );
          setError({ message: lines.join('\n') });
        }

        const newMetadataMap: Record<string, WorkspaceMetadata> = {};
        const oldMetadataMap = localData.workspaceMetadataMap || {};
        for (const key of Object.keys(oldMetadataMap)) {
          if (newWorkspaceMap[key]) {
            newMetadataMap[key] = oldMetadataMap[key];
          }
        }

        setCart(newCart);
        setLocalLoaded(true);
        setLocalLoading(false);
        setSelectedMap(newSelectedMap);
        setWorkspaceMap(newWorkspaceMap);
        setWorkspaceMetadataMap(newMetadataMap);
        if (
          !isEmpty({ cart: newCart, selectedMap: newSelectedMap, workspaceMap: newWorkspaceMap })
        ) {
          setDate(localDate);
        }
      })();
    }
  }, [
    company,
    creativeMap,
    date,
    isciLiveOnDate,
    localLoaded,
    localLoading,
    localStorageKey,
    setCart,
    setError,
    setSelectedMap,
    setWorkspaceMap,
    setWorkspaceMetadataMap,
    shopData
  ]);

  const importCSVRotations = useCallback(() => {
    if (!file) {
      return;
    }
    const fileReader = new FileReader();
    fileReader.onload = event => {
      const result = event.target?.result;
      if (typeof result !== 'string') {
        return;
      }
      const rows = result.split('\n');
      // Check if the data is empty (need more than just a header).
      if (rows.length < 2) {
        setError({
          message: 'There is no data in the file provided, please check the csv uploaded.',
          title: 'Error: No Data'
        });
        return;
      }
      if (!shopData || !Object.keys(shopData.placementMap).length) {
        setError({
          message:
            'There is no placement data available yet, please wait. If this issue is persisting please contact the engineering team.',
          title: 'Error: No Placement Data'
        });
      }

      // Only eligible creatives are valid CSV targets (workspaceOptions now also
      // includes ineligible creatives, annotated with a reason).
      const workspaceOptionsMap: Record<string, boolean> = {};
      for (const option of workspaceOptions ?? []) {
        if (!option.reason) {
          workspaceOptionsMap[option.name] = true;
        }
      }

      const newCart: CartItem[] = [];
      let excelRowIndex = 2;
      for (const rowString of rows.slice(1)) {
        setSelectedMap({});
        setWorkspaceMap({});
        setWorkspaceMetadataMap({});
        if (!rowString) {
          setError({
            message: `There is no data in row: ${excelRowIndex}.\n              Please check your csv file.`,
            title: 'Error: No Row Data'
          });
        }

        const rotation: { isci: string; value: number }[] = [];
        const placements: string[] = [];
        const row = rowString.split(',');
        const placementIDs = row[0].split(',');
        setDate(row[1]);
        let creative: string | null = null;

        // Verify the placement ids used.
        for (const placementID of placementIDs) {
          if (shopData?.placementMap?.[placementID]) {
            placements.push(placementID);
          } else {
            setError({
              message: `The following placement ID is invalid: ${placementID}. Skipping this id.\n                Please check csv row: ${excelRowIndex}.`,
              title: 'Error: Placement ID'
            });
          }
        }

        // Extract and verify the creative rotation for a given set of placements.
        let runningTotal = 0;
        for (let i = 2; i < row.length; i++) {
          if (i % 2 && creative) {
            rotation.push({ isci: creative, value: Number(row[i]) });
            runningTotal += parseInt(row[i], 10);
          } else if (i % 2 === 0) {
            creative = row[i] && workspaceOptionsMap[row[i]] ? row[i] : null;
            if (row[i] && !workspaceOptionsMap[row[i]]) {
              setError({
                message: `The following isci: ${row[i]} is not valid. \n                  Please check csv row: ${excelRowIndex}.`,
                title: 'Error: ISCI'
              });
              return;
            }
          }
        }

        // Verify the creative allocation.
        if (runningTotal !== 100 && Math.round(runningTotal) !== 100) {
          setError({
            message: `Please check csv row: ${excelRowIndex}. The creative allocation for this row\n              doesn't add up to 100. Getting: ${runningTotal}`,
            title: 'Error: Creative Allocation'
          });
        }

        newCart.push({ id: Date.now(), placements: [...new Set(placements)], rotation });
        excelRowIndex++;
      }
      setCart(newCart);
      goToTab(TabKey.CART);
    };
    fileReader.readAsText(file);
  }, [
    file,
    goToTab,
    setCart,
    setError,
    setSelectedMap,
    setWorkspaceMap,
    setWorkspaceMetadataMap,
    shopData,
    workspaceOptions
  ]);

  useEffect(() => {
    if (localLoaded) {
      window.localStorage.setItem(
        localStorageKey,
        JSON.stringify({ selectedMap, workspaceMap, workspaceMetadataMap, cart, date })
      );
    }
  }, [selectedMap, workspaceMap, cart, date, localLoaded, localStorageKey, workspaceMetadataMap]);

  const [committing, setCommitting] = useState(false);

  const [approvalStageData, setApprovalStageData] = useState<ApprovalStageData>();

  useEffect(() => {
    (async () => {
      if (company && !committing) {
        try {
          const res = await StreamingLambdaFetch('/getCreativeApprovalStage', {
            params: { company }
          });
          const data = await awaitJSON<ApprovalStageData>(res);
          setApprovalStageData(data);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      }
    })();
  }, [company, setError, committing, tab]);

  const updateApprovalStage = useCallback(
    (
      currentCompany: string,
      newApprovalStage: ApprovalStages | string,
      selectedReviewers: Reviewer[],
      currentRequester: string,
      currentReviewer?: string | null,
      commentBoxText?: string
    ) => {
      (async () => {
        try {
          const res = await StreamingLambdaFetch('/updateCreativeApprovalStage', {
            method: 'POST',
            body: {
              company: currentCompany,
              stage: newApprovalStage,
              reviewers: selectedReviewers,
              requester: currentRequester,
              currentReviewer,
              commentBoxText
            }
          });
          const data = await awaitJSON<ApprovalStageData>(res);
          setApprovalStageData(data);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    },
    [setError]
  );

  const commit = useCallback(async () => {
    setCommitting(true);
    try {
      const placements: {
        placementID: string;
        creatives: { isci: string; pct: number }[];
        clickthroughUrlMap: Record<string, string | undefined>;
        audioCompanionMap: Record<string, CommitAudioCompanion>;
        extraTrackingPixels: string[];
      }[] = [];
      for (const { placements: cartPlacements, rotation } of cart) {
        const creatives: { isci: string; pct: number }[] = [];
        const clickthroughUrlMap: Record<string, string | undefined> = {};
        for (const creative of rotation) {
          creatives.push({ isci: creative.isci, pct: creative.value });
          clickthroughUrlMap[creative.isci] = creative.clickthroughURL;
        }
        const audioCompanionMap = buildAudioCompanionMap(rotation);
        for (const placementID of cartPlacements) {
          placements.push({
            placementID,
            creatives,
            clickthroughUrlMap,
            audioCompanionMap,
            extraTrackingPixels: []
          });
        }
      }

      const res = await CreativeLambdaFetch('/commitCreatives', {
        method: 'POST',
        body: { date, company, placements }
      });
      await awaitJSON(res);

      await refetchShopData();

      if (
        approvalStageData &&
        approvalStageData.stage !== ApprovalStages.CHANGES_REQUESTED &&
        approvalStageData.stage !== ApprovalStages.PRE_REVIEW
      ) {
        setApprovalStageData(undefined);
        // Pass in reviewers and requester so they will be sent a slack saying the
        // process was canceled (if they exist).
        updateApprovalStage(
          company,
          ApprovalStages.PRE_REVIEW,
          approvalStageData.reviewers || [],
          approvalStageData.requester || ''
        );
      }

      goToTab(TabKey.APPROVALS);
      setSelectedMap({});
      setCart([]);
      setWorkspaceMap({});
      setWorkspaceMetadataMap({});
      setPlatform('');
      setAdServer('');
      setDimensions('');
      setLength(null);
      setFlashtalkingPlacementType('');
    } catch (e) {
      setError({ message: `Failed to commit changes. Error: ${e.message}`, reportError: e });
    }
    setCommitting(false);
  }, [
    date,
    company,
    refetchShopData,
    approvalStageData,
    goToTab,
    setSelectedMap,
    setCart,
    setWorkspaceMap,
    setWorkspaceMetadataMap,
    updateApprovalStage,
    cart,
    setError
  ]);

  const setDatePicker = useCallback(
    (newDate: string) => {
      const nonEmptyCart = Boolean(cart.length);
      const nonEmptyWorkspace = Object.values(workspaceMap).some(Boolean);
      if (nonEmptyCart || nonEmptyWorkspace) {
        const whatYouHave: string[] = [];
        if (nonEmptyCart) {
          whatYouHave.push('items in your cart');
        }
        if (nonEmptyWorkspace) {
          whatYouHave.push('values in your workspace');
        }

        setAreYouSure({
          title: 'Changing dates with non-empty cart',
          message: `You have ${whatYouHave.join(
            ' and '
          )}. Changing the date will clear these. Are you sure you want to continue?`,
          okayText: 'Yes, change the date',
          onOkay: () => {
            clearWorkspace();
            setDate(newDate);
          }
        });
      } else {
        setDate(newDate);
      }
    },
    [cart, workspaceMap, setAreYouSure, clearWorkspace]
  );

  const contextValue = useMemo<StreamingCreativesContextValue | null>(() => {
    if (!shopData) {
      return null;
    }
    return {
      tab,
      goToTab,
      date,
      shopData,
      cart,
      selectedMap,
      inCart,
      setSelectedValue,
      setSelectedMap,
      workspaceOptions: workspaceOptions ?? [],
      workspaceMap,
      setWorkspaceValue,
      setWorkspaceMap,
      workspaceMetadataMap,
      setWorkspaceMetadata,
      setWorkspaceMetadataMap,
      addToCart,
      committing,
      commit,
      setCart,
      audioCompanionOptions,
      approvalStageData,
      updateApprovalStage,
      platform,
      setPlatform,
      adServer,
      setAdServer,
      dimensions,
      setDimensions,
      length,
      setLength,
      flashtalkingPlacementType,
      setFlashtalkingPlacementType
    };
  }, [
    tab,
    goToTab,
    date,
    shopData,
    cart,
    selectedMap,
    inCart,
    setSelectedValue,
    setSelectedMap,
    workspaceOptions,
    workspaceMap,
    setWorkspaceValue,
    setWorkspaceMap,
    workspaceMetadataMap,
    setWorkspaceMetadata,
    setWorkspaceMetadataMap,
    addToCart,
    committing,
    commit,
    setCart,
    audioCompanionOptions,
    approvalStageData,
    updateApprovalStage,
    platform,
    adServer,
    dimensions,
    length,
    flashtalkingPlacementType
  ]);

  return (
    <Page
      title='Streaming Creatives'
      pageType='Streaming Creatives'
      navs={[
        { label: 'Menu', key: TabKey.MENU },
        { label: 'Workspace', key: TabKey.WORKSPACE },
        { label: `Cart${cart.length ? ` (${cart.length})` : ''}`, key: TabKey.CART },
        { label: 'Approvals', key: TabKey.APPROVALS },
        { label: 'Generate', key: TabKey.GENERATE }
      ]}
      selectedNav={tab}
      onNav={key => goToTab(key as TabKeyType)}
      actions={
        <div className='streaming-creative-actions'>
          {enableImporter && (
            <Button
              size={Size.LG}
              className='import-csv-button'
              onClick={() => setShowImportChangesModal(true)}
            >
              Import Creative Allocations
            </Button>
          )}
          {enableImporter && showImportChangesModal && (
            <ImportModal
              importFunction={importCSVRotations}
              file={file}
              setFile={setFile}
              onClose={() => setShowImportChangesModal(false)}
            />
          )}
          <div className='date-picker-label'>
            Rotation Date:
            <InfoTooltip iconColor={Neutral100} placement={Placement.BOTTOM}>
              Any rotations on this page will be what the rotations will be on this date. Any
              rotations you set here will start on this date.
            </InfoTooltip>
          </div>
          <DatePicker
            isSingle
            appearance={Appearance.FILLED}
            design={Design.NEUTRAL}
            range={{ start: date, end: date }}
            onChange={({ start }) => setDatePicker(start)}
            isOutsideRange={day => day < TODAY}
          />
        </div>
      }
    >
      <div className='streaming-creatives'>
        {contextValue && localLoaded && creativeMapLoaded ? (
          <StreamingCreativesContext.Provider value={contextValue}>
            <div data-testid='streaming-creatives-content' className='streaming-creatives-content'>
              {tab === TabKey.CART && <Cart />}
              {tab === TabKey.APPROVALS && <Approvals />}
              {tab === TabKey.GENERATE && <Generate />}
              {(tab === TabKey.MENU || tab === TabKey.WORKSPACE) && <NonCart />}
            </div>
          </StreamingCreativesContext.Provider>
        ) : (
          <FullPageSpinner />
        )}
      </div>
    </Page>
  );
};
